/** @jsx h */
import { component, h } from 'literaljs';
import { navigate } from 'literaljs-router';
import { playBloop } from '../../utils/sounds';
import { getTheme, toggleTheme } from '../../utils/theme';

export default component({
	name: 'Navbar',
	state: { showMenu: false, currentPath: '/', theme: 'dark' },
	mounted() {
		const cleanPath = (p) => (p || '').split('?')[0].split('#')[0] || '/';
		this._onRoute = (e) => {
			const path = cleanPath(e.detail?.path || window.location.pathname);
			this.setState({ currentPath: path, showMenu: false });
		};
		window.addEventListener('literaljs-route', this._onRoute);
		this.setState({
			currentPath: cleanPath(window.location.pathname),
			theme: getTheme()
		});
		// Listen for Escape key to close mobile menu
		this._onKeydown = (e) => {
			if (e.key === 'Escape' && this.getState().showMenu) {
				this.setState({ showMenu: false });
			}
		};
		document.addEventListener('keydown', this._onKeydown);
		// Persistent focus trap handler - uses getState() for current value
		this._focusTrapHandler = (e) => {
			if (e.key !== 'Tab') return;
			if (!this.getState().showMenu) return;
			const mobileNav = document.getElementById('mobile-nav');
			if (!mobileNav) return;
			const focusableElements = mobileNav.querySelectorAll('a, button');
			if (focusableElements.length === 0) return;
			const firstElement = focusableElements[0];
			const lastElement = focusableElements[focusableElements.length - 1];
			
			if (e.shiftKey && document.activeElement === firstElement) {
				e.preventDefault();
				lastElement.focus();
			} else if (!e.shiftKey && document.activeElement === lastElement) {
				e.preventDefault();
				firstElement.focus();
			}
		};
		document.addEventListener('keydown', this._focusTrapHandler);
		this._touchStartX = 0;
		this._touchStartY = 0;
		this._swipeThreshold = 80;
		this._edgeThreshold = 50;
		this._isMobile = () => window.innerWidth < 768;
		this._onTouchStart = (e) => {
			this._touchStartX = e.touches[0].clientX;
			this._touchStartY = e.touches[0].clientY;
		};
		this._onTouchEnd = (e) => {
			if (!this._isMobile()) return;
			const touchEndX = e.changedTouches[0].clientX;
			const touchEndY = e.changedTouches[0].clientY;
			const deltaX = touchEndX - this._touchStartX;
			const deltaY = touchEndY - this._touchStartY;
			if (Math.abs(deltaY) > Math.abs(deltaX)) return;
			const { showMenu } = this.getState();
			const target = e.target;
			const mobileNav = document.getElementById('mobile-nav');
			const isSwipeRight = deltaX > this._swipeThreshold;
			const isSwipeLeft = deltaX < -this._swipeThreshold;
			if (isSwipeRight && !showMenu && this._touchStartX < this._edgeThreshold) {
				this.toggleMenu();
				return;
			}
			if (isSwipeLeft && showMenu && mobileNav && mobileNav.contains(target)) {
				this.closeMenu();
				return;
			}
			if (isSwipeLeft && showMenu && mobileNav && !mobileNav.contains(target)) {
				this.closeMenu();
				return;
			}
		};
		document.addEventListener('touchstart', this._onTouchStart, { passive: true });
		document.addEventListener('touchend', this._onTouchEnd, { passive: true });
	},
	unmounted() {
		if (this._onRoute) {
			window.removeEventListener('literaljs-route', this._onRoute);
		}
		if (this._onKeydown) {
			document.removeEventListener('keydown', this._onKeydown);
		}
		if (this._focusTrapHandler) {
			document.removeEventListener('keydown', this._focusTrapHandler);
		}
		if (this._onTouchStart) {
			document.removeEventListener('touchstart', this._onTouchStart);
		}
		if (this._onTouchEnd) {
			document.removeEventListener('touchend', this._onTouchEnd);
		}
		document.body.style.overflow = '';
	},
	methods() {
		return {
			goTo(path) {
				playBloop(1.1);
				this.setState({ showMenu: false });
				navigate(path);
			},
			toggleMenu() {
				playBloop(0.8);
				const newShowMenu = !this.getState().showMenu;
				this.setState({ showMenu: newShowMenu });
			},
			closeMenu() {
				this.setState({ showMenu: false });
			},
			toggleTheme() {
				playBloop(1.0);
				const newTheme = toggleTheme();
				this.setState({ theme: newTheme });
			}
		};
	},
	render() {
		const { showMenu, currentPath, theme } = this.getState();
		
		if (showMenu) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		
		if (showMenu) {
			setTimeout(() => {
				const mobileNav = document.getElementById('mobile-nav');
				if (mobileNav) {
					const firstFocusable = mobileNav.querySelector('a, button');
					if (firstFocusable) firstFocusable.focus();
				}
			}, 50);
		} else if (this._menuWasOpen) {
			const menuButton = document.querySelector('.MenuButton');
			if (menuButton) menuButton.focus();
		}
		this._menuWasOpen = showMenu;
		
		const navItems = [
			{ path: '/', text: 'Home' },
			{ path: '/docs', text: 'Docs' },
			{ path: '/learn', text: 'Learn' },
			{ path: '/examples', text: 'Examples' },
			{ path: '/changelog', text: 'Changelog' }
		];

		return (
			<div style={{ display: 'contents' }}>
				<nav class="Navbar">
					<div class="brand" style={{ cursor: 'pointer' }} events={{ click: () => this.goTo('/') }}>
						<div class="logo-mark">
							<span class="L">L</span>
							<span class="J">J</span>
							<span class="S">S</span>
						</div>
						<span>LiteralJS</span>
					</div>
					<span class="version-badge">v8.1.11</span>
					<div class="nav-links">
						{navItems.map(item => (
						<a
							key={item.path}
							href={item.path}
							class={`nav-link ${currentPath === item.path ? '-active' : ''}`}
							events={{
								click: (e) => { e.preventDefault(); this.goTo(item.path); }
							}}
						>{item.text}</a>
						))}
						<button
							class="theme-toggle"
							events={{ click: () => this.toggleTheme() }}
							aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
						>
							{theme === 'dark' ? '🌙' : '☀️'}
						</button>
					</div>
				<button
					class={`MenuButton ${showMenu ? '-active' : ''}`}
					events={{ click: () => this.toggleMenu() }}
					aria-label="Toggle navigation menu"
					aria-expanded={showMenu}
					aria-controls="mobile-nav"
				>
					<span></span>
					<span></span>
					<span></span>
				</button>
			</nav>
			{/* Theme FAB — only visible on mobile (≤ 828px, matches new navbar breakpoint) */}
			<button
				class="theme-fab"
				events={{ click: () => this.toggleTheme() }}
				aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
			>
				{theme === 'dark' ? '🌙' : '☀️'}
			</button>
			<div
				class={`mobile-nav-overlay ${showMenu ? '-active' : ''}`}
				events={{ click: () => this.closeMenu() }}
				aria-hidden="true"
			></div>
			<div
				id="mobile-nav"
				class={`mobile-nav ${showMenu ? '-active' : ''}`}
				role="dialog"
				aria-label="Navigation menu"
				aria-modal="true"
			>
				<button 
					class="mobile-nav-close"
					events={{ click: () => this.closeMenu() }}
					aria-label="Close navigation menu"
				>
					<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<line x1="18" y1="6" x2="6" y2="18"></line>
						<line x1="6" y1="6" x2="18" y2="18"></line>
					</svg>
				</button>
				<div class="mobile-nav-content">
					{navItems.map(item => (
						<a
							key={item.path}
							href={item.path}
							class={`nav-link ${currentPath === item.path ? '-active' : ''}`}
							events={{
								click: (e) => { e.preventDefault(); this.goTo(item.path); }
							}}
						>
							{item.text}
						</a>
					))}
					<button 
						class="nav-cta btn -primary"
						events={{ click: () => this.toggleTheme() }}
						aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
					>
						<span>{theme === 'dark' ? '🌙' : '☀️'}</span>
						{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
					</button>
				</div>
			</div>
			</div>
		);
	}
});